/**
 * Fund Explorer Screen
 *
 * Main fund discovery page with:
 * - Search by fund name or ISIN
 * - Filtering by asset class, category, plan type, rating
 * - Sorting by various metrics
 * - Category grouping toggle
 * - Pagination
 * - Multi-fund selection for comparison
 *
 * Route: /explore
 */

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PullToRefresh } from "@/components/ui/pull-to-refresh";
import {
  FilterBar,
  FilterBarSkeleton,
  DEFAULT_FILTER_STATE,
  type FilterState,
  type AssetClass,
} from "@/components/filters";
import { FundSearchInput, FundSearchInputSkeleton } from "@/components/fund-search";
import {
  FundListItem,
  FundListItemSkeleton,
  type FundListItemData,
} from "@/components/funds";
import {
  useFunds,
  usePullToRefresh,
  getFundsErrorMessage,
  type FundsFilterParams,
  type FundListItem as FundListItemType,
} from "@/hooks";
import type { FundCategory, FundPlanType, FundRating } from "@curie/shared";
import {
  RefreshCw,
  AlertCircle,
  Search,
  GitCompare,
  LayoutGrid,
  LayoutList,
  ChevronLeft,
  ChevronRight,
  Layers,
} from "lucide-react";
import { GlobalErrorFallback } from "@/components/error";

// ============================================================================
// Route Definition
// ============================================================================

export const Route = createFileRoute("/explore/")({
  component: FundExplorerPage,
  errorComponent: GlobalErrorFallback,
});

// ============================================================================
// Constants
// ============================================================================

const ITEMS_PER_PAGE = 20;

/**
 * Category labels for grouping display
 */
const CATEGORY_LABELS: Record<FundCategory, string> = {
  "equity-large-cap": "Large Cap",
  "equity-mid-cap": "Mid Cap",
  "equity-small-cap": "Small Cap",
  "equity-flexi-cap": "Flexi Cap",
  "equity-multi-cap": "Multi Cap",
  "equity-elss": "ELSS",
  "equity-sectoral": "Sectoral",
  "equity-global": "Global",
  "debt-liquid": "Liquid",
  "debt-short-term": "Short Term",
  "debt-medium-term": "Medium Term",
  "debt-long-term": "Long Term",
  "debt-gilt": "Gilt",
  "debt-corporate": "Corporate",
  "hybrid-aggressive": "Aggressive Hybrid",
  "hybrid-balanced": "Balanced Hybrid",
  "hybrid-conservative": "Conservative Hybrid",
};

/**
 * Asset class labels
 */
const ASSET_CLASS_LABELS: Record<string, string> = {
  equity: "Equity Funds",
  debt: "Debt Funds",
  hybrid: "Hybrid Funds",
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Transform API fund item to component data format
 */
function transformToFundListItemData(fund: FundListItemType): FundListItemData {
  return {
    isin: fund.isin,
    rank: fund.rank,
    name: fund.name,
    category: fund.category,
    amcName: fund.amcName,
    rating: fund.rating,
    score: fund.score,
    returns1Y: fund.returns1Y,
    returns3Y: fund.returns3Y,
    returns5Y: fund.returns5Y,
    aum: fund.aum,
    expenseRatio: fund.expenseRatio,
    exitLoad: fund.exitLoad,
  };
}

/**
 * Convert FilterState to FundsFilterParams for the API
 */
function filterStateToParams(
  filterState: FilterState,
  searchQuery: string,
  page: number
): FundsFilterParams {
  const params: FundsFilterParams = {
    page,
    limit: ITEMS_PER_PAGE,
    sortBy: filterState.sortField,
    sortOrder: filterState.sortDirection,
  };

  // Add search query
  if (searchQuery.trim()) {
    params.query = searchQuery.trim();
  }

  // Add category filter (first category if multiple selected)
  if (filterState.categories.length > 0) {
    params.category = filterState.categories[0];
  }

  // Add plan type filter
  if (filterState.directOnly) {
    params.plan = "direct";
  }

  // Add rating filter (first rating if multiple selected)
  if (filterState.ratings.length > 0) {
    params.rating = filterState.ratings[0];
  }

  return params;
}

/**
 * Get asset class from category
 */
function getAssetClassFromCategory(category: string): string {
  const prefix = category.split("-")[0];
  return prefix || "other";
}

/**
 * Group funds by category
 */
function groupFundsByCategory(
  funds: FundListItemData[]
): Record<string, FundListItemData[]> {
  return funds.reduce(
    (acc, fund) => {
      const category = fund.category;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(fund);
      return acc;
    },
    {} as Record<string, FundListItemData[]>
  );
}

/**
 * Group funds by asset class
 */
function groupFundsByAssetClass(
  funds: FundListItemData[]
): Record<string, FundListItemData[]> {
  return funds.reduce(
    (acc, fund) => {
      const assetClass = getAssetClassFromCategory(fund.category);
      if (!acc[assetClass]) {
        acc[assetClass] = [];
      }
      acc[assetClass].push(fund);
      return acc;
    },
    {} as Record<string, FundListItemData[]>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Error display component
 */
function ErrorDisplay({
  message,
  onRetry,
  isRetrying,
}: {
  message: string;
  onRetry: () => void;
  isRetrying?: boolean;
}) {
  return (
    <Card className="border-destructive/50 bg-destructive/5">
      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Failed to load funds
        </h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-md">{message}</p>
        <Button
          variant="outline"
          onClick={onRetry}
          disabled={isRetrying}
          className="gap-2"
        >
          <RefreshCw
            className={`h-4 w-4 ${isRetrying ? "animate-spin" : ""}`}
          />
          {isRetrying ? "Retrying..." : "Try Again"}
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * Empty state component
 */
function EmptyState({ hasFilters }: { hasFilters: boolean }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-16 text-center">
        <Search className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {hasFilters ? "No funds match your filters" : "No funds found"}
        </h3>
        <p className="text-sm text-muted-foreground max-w-md">
          {hasFilters
            ? "Try adjusting your search criteria or clearing some filters to see more results."
            : "Start by searching for a fund name or browse by category."}
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * Loading skeleton for the fund list
 */
function FundListLoadingSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <FundListItemSkeleton key={i} showRank={true} />
      ))}
    </div>
  );
}

/**
 * Pagination component
 */
function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  isLoading,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
}) {
  if (totalPages <= 1) return null;

  // Calculate page range to show
  const maxPagesToShow = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
  const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

  // Adjust start if we're near the end
  if (endPage - startPage < maxPagesToShow - 1) {
    startPage = Math.max(1, endPage - maxPagesToShow + 1);
  }

  const pages: number[] = [];
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <div className="flex items-center justify-center gap-1 py-4">
      <Button
        variant="outline"
        size="icon-sm"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1 || isLoading}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      {startPage > 1 && (
        <>
          <Button
            variant={currentPage === 1 ? "default" : "ghost"}
            size="sm"
            onClick={() => onPageChange(1)}
            disabled={isLoading}
          >
            1
          </Button>
          {startPage > 2 && (
            <span className="px-2 text-muted-foreground">...</span>
          )}
        </>
      )}

      {pages.map((page) => (
        <Button
          key={page}
          variant={currentPage === page ? "default" : "ghost"}
          size="sm"
          onClick={() => onPageChange(page)}
          disabled={isLoading}
        >
          {page}
        </Button>
      ))}

      {endPage < totalPages && (
        <>
          {endPage < totalPages - 1 && (
            <span className="px-2 text-muted-foreground">...</span>
          )}
          <Button
            variant={currentPage === totalPages ? "default" : "ghost"}
            size="sm"
            onClick={() => onPageChange(totalPages)}
            disabled={isLoading}
          >
            {totalPages}
          </Button>
        </>
      )}

      <Button
        variant="outline"
        size="icon-sm"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages || isLoading}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

/**
 * Fund selection checkbox overlay
 */
function FundSelectionCheckbox({
  isSelected,
  onToggle,
}: {
  isSelected: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className={`
        absolute top-3 left-3 z-10 h-6 w-6 rounded-md border-2
        flex items-center justify-center transition-all
        ${
          isSelected
            ? "bg-primary border-primary text-primary-foreground"
            : "bg-background border-muted-foreground/30 hover:border-muted-foreground/50"
        }
      `}
      aria-label={isSelected ? "Deselect fund" : "Select fund for comparison"}
    >
      {isSelected && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      )}
    </button>
  );
}

/**
 * Compare floating action bar
 */
function CompareActionBar({
  selectedCount,
  onCompare,
  onClear,
}: {
  selectedCount: number;
  onCompare: () => void;
  onClear: () => void;
}) {
  if (selectedCount === 0) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50">
      <div className="flex items-center gap-3 bg-background/95 backdrop-blur-sm border shadow-lg rounded-full px-4 py-2">
        <span className="text-sm font-medium">
          {selectedCount} fund{selectedCount !== 1 ? "s" : ""} selected
        </span>
        <div className="h-4 w-px bg-border" />
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          className="text-muted-foreground"
        >
          Clear
        </Button>
        <Button
          size="sm"
          onClick={onCompare}
          disabled={selectedCount < 2}
          className="gap-2"
        >
          <GitCompare className="h-4 w-4" />
          Compare
        </Button>
      </div>
    </div>
  );
}

/**
 * Category group header
 */
function CategoryGroupHeader({
  category,
  count,
}: {
  category: string;
  count: number;
}) {
  const label = CATEGORY_LABELS[category as FundCategory] || category;
  return (
    <div className="flex items-center gap-2 py-3 sticky top-0 bg-background/95 backdrop-blur-sm z-10">
      <h3 className="text-lg font-semibold text-foreground">{label}</h3>
      <span className="text-sm text-muted-foreground">({count} funds)</span>
    </div>
  );
}

/**
 * Asset class group header
 */
function AssetClassGroupHeader({
  assetClass,
  count,
}: {
  assetClass: string;
  count: number;
}) {
  const label = ASSET_CLASS_LABELS[assetClass] || `${assetClass} Funds`;
  return (
    <div className="flex items-center gap-2 py-4 sticky top-0 bg-background/95 backdrop-blur-sm z-10 border-b">
      <h2 className="text-xl font-bold text-foreground">{label}</h2>
      <span className="text-sm text-muted-foreground">({count} funds)</span>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

function FundExplorerPage() {
  const navigate = useNavigate();

  // State
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTER_STATE);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFunds, setSelectedFunds] = useState<Set<string>>(new Set());
  const [groupByCategory, setGroupByCategory] = useState(false);

  // Build API params from filter state
  const apiParams = useMemo(
    () => filterStateToParams(filters, searchQuery, currentPage),
    [filters, searchQuery, currentPage]
  );

  // Fetch funds data
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useFunds(apiParams);

  // Pull to refresh
  const { isRefreshing, handleRefresh } = usePullToRefresh({
    onRefresh: refetch,
    isFetching,
  });

  // Transform funds data
  const funds = useMemo(() => {
    return (data?.funds ?? []).map(transformToFundListItemData);
  }, [data?.funds]);

  // Pagination info
  const pagination = data?.pagination;
  const totalPages = pagination?.totalPages ?? 1;
  const totalCount = pagination?.totalCount ?? 0;

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      searchQuery.trim() !== "" ||
      filters.assetClass !== "all" ||
      filters.categories.length > 0 ||
      filters.directOnly ||
      filters.ratings.length > 0
    );
  }, [searchQuery, filters]);

  // Grouped funds
  const groupedFunds = useMemo(() => {
    if (!groupByCategory) return null;
    return groupFundsByCategory(funds);
  }, [funds, groupByCategory]);

  // Handle search
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page on search
  }, []);

  // Handle filter change
  const handleFiltersChange = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page on filter change
  }, []);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    // Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Handle fund click
  const handleFundClick = useCallback(
    (isin: string) => {
      navigate({ to: "/explore/$fundId", params: { fundId: isin } });
    },
    [navigate]
  );

  // Handle fund selection toggle
  const handleToggleSelection = useCallback((isin: string) => {
    setSelectedFunds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(isin)) {
        newSet.delete(isin);
      } else {
        // Limit to 4 funds for comparison
        if (newSet.size < 4) {
          newSet.add(isin);
        }
      }
      return newSet;
    });
  }, []);

  // Handle compare button click
  const handleCompare = useCallback(() => {
    const fundsArray = Array.from(selectedFunds);
    if (fundsArray.length >= 2) {
      navigate({
        to: "/compare",
        search: { funds: fundsArray },
      });
    }
  }, [selectedFunds, navigate]);

  // Clear selected funds
  const handleClearSelection = useCallback(() => {
    setSelectedFunds(new Set());
  }, []);

  // Toggle category grouping
  const handleToggleGrouping = useCallback(() => {
    setGroupByCategory((prev) => !prev);
  }, []);

  // Render loading state
  if (isLoading) {
    return (
      <main className="container mx-auto max-w-4xl px-4 py-6">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground">Explore Funds</h1>
          <p className="mt-1 text-muted-foreground">
            Discover and compare mutual funds
          </p>
        </div>

        <div className="space-y-4">
          <FundSearchInputSkeleton />
          <FilterBarSkeleton />
          <FundListLoadingSkeleton count={5} />
        </div>
      </main>
    );
  }

  // Render error state
  if (isError) {
    return (
      <main className="container mx-auto max-w-4xl px-4 py-6">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground">Explore Funds</h1>
          <p className="mt-1 text-muted-foreground">
            Discover and compare mutual funds
          </p>
        </div>

        <ErrorDisplay
          message={getFundsErrorMessage(error)}
          onRetry={() => refetch()}
          isRetrying={isFetching}
        />
      </main>
    );
  }

  return (
    <PullToRefresh
      onRefresh={handleRefresh}
      isRefreshing={isRefreshing}
      className="h-full"
    >
      <main className="container mx-auto max-w-4xl px-4 py-6 pb-24">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Explore Funds</h1>
              <p className="mt-1 text-muted-foreground">
                Discover and compare mutual funds
              </p>
            </div>
            {(isRefreshing || isFetching) && !isLoading && (
              <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
            )}
          </div>
        </div>

        {/* Search Input */}
        <div className="mb-4">
          <FundSearchInput
            onSearch={handleSearch}
            isLoading={isFetching}
            placeholder="Search by fund name or ISIN..."
            value={searchQuery}
            onChange={setSearchQuery}
          />
        </div>

        {/* Filter Bar */}
        <div className="mb-4">
          <FilterBar
            filters={filters}
            onFiltersChange={handleFiltersChange}
          />
        </div>

        {/* Results Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-muted-foreground">
            {totalCount > 0 ? (
              <>
                Showing{" "}
                <span className="font-medium text-foreground">
                  {(currentPage - 1) * ITEMS_PER_PAGE + 1}-
                  {Math.min(currentPage * ITEMS_PER_PAGE, totalCount)}
                </span>{" "}
                of{" "}
                <span className="font-medium text-foreground">{totalCount}</span>{" "}
                funds
              </>
            ) : (
              "No funds found"
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Grouping Toggle */}
            <Button
              variant={groupByCategory ? "secondary" : "ghost"}
              size="sm"
              onClick={handleToggleGrouping}
              className="gap-2"
              title={groupByCategory ? "Show as list" : "Group by category"}
            >
              <Layers className="h-4 w-4" />
              <span className="hidden sm:inline">
                {groupByCategory ? "Grouped" : "Group"}
              </span>
            </Button>
          </div>
        </div>

        {/* Fund List */}
        {funds.length === 0 ? (
          <EmptyState hasFilters={hasActiveFilters} />
        ) : groupByCategory && groupedFunds ? (
          // Grouped view
          <div className="space-y-6">
            {Object.entries(groupedFunds).map(([category, categoryFunds]) => (
              <div key={category}>
                <CategoryGroupHeader
                  category={category}
                  count={categoryFunds.length}
                />
                <div className="space-y-3">
                  {categoryFunds.map((fund) => (
                    <div key={fund.isin} className="relative">
                      <FundSelectionCheckbox
                        isSelected={selectedFunds.has(fund.isin)}
                        onToggle={() => handleToggleSelection(fund.isin)}
                      />
                      <div className="pl-10">
                        <FundListItem
                          data={fund}
                          onClick={handleFundClick}
                          showRank={true}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // List view
          <div className="space-y-3">
            {funds.map((fund) => (
              <div key={fund.isin} className="relative">
                <FundSelectionCheckbox
                  isSelected={selectedFunds.has(fund.isin)}
                  onToggle={() => handleToggleSelection(fund.isin)}
                />
                <div className="pl-10">
                  <FundListItem
                    data={fund}
                    onClick={handleFundClick}
                    showRank={true}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!groupByCategory && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            isLoading={isFetching}
          />
        )}

        {/* Compare Action Bar */}
        <CompareActionBar
          selectedCount={selectedFunds.size}
          onCompare={handleCompare}
          onClear={handleClearSelection}
        />
      </main>
    </PullToRefresh>
  );
}
